import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOData, getCanonicalUrl, getHreflangTags } from '../../config/seoConfig';

const SEOHead = ({ customTitle, customDescription, customImage }) => {
  const location = useLocation();
  const seoData = getSEOData(location.pathname);
  
  // Use custom values if provided, otherwise use config
  const title = customTitle || seoData.title;
  const description = customDescription || seoData.description;
  const ogImage = customImage || seoData.ogImage || '/default-og-image.png';
  const canonicalUrl = getCanonicalUrl(location.pathname);
  const hreflangTags = getHreflangTags(location.pathname);
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
      
      {/* Language */}
      {seoData.lang && <html lang={seoData.lang} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags for international SEO */}
      {hreflangTags.map((tag, index) => (
        <link key={index} rel={tag.rel} hreflang={tag.hreflang} href={tag.href} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoData.ogTitle || title} />
      <meta property="og:description" content={seoData.ogDescription || description} />
      <meta property="og:image" content={`https://pickstarai.com${ogImage}`} />
      {seoData.ogLocale && <meta property="og:locale" content={seoData.ogLocale} />}
      <meta property="og:site_name" content="PickStar AI" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={seoData.twitterCard || "summary_large_image"} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoData.ogTitle || title} />
      <meta name="twitter:description" content={seoData.ogDescription || description} />
      <meta name="twitter:image" content={`https://pickstarai.com${ogImage}`} />
      <meta name="twitter:site" content="@PickStarAI" />
      
      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="google" content="notranslate" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* JSON-LD Structured Data */}
      {seoData.jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(seoData.jsonLd)}
        </script>
      )}
      
      {/* Additional Organization Schema (appears on all pages) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "PickStar Labs LLC",
          "url": "https://pickstarai.com",
          "logo": "https://pickstarai.com/logo512.png",
          "description": "AI-powered sports betting predictions and analysis platform",
          "sameAs": [
            "https://twitter.com/PickStarAI",
            "https://www.youtube.com/@PickStarAI",
            "https://www.reddit.com/r/PickStar/",
            "https://www.instagram.com/pickstarai/"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-555-123-4567",
            "contactType": "customer service",
            "availableLanguage": ["English", "Portuguese"]
          }
        })}
      </script>
      
      {/* Breadcrumb Schema for better navigation in search results */}
      {location.pathname !== '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": location.pathname.split('/').filter(Boolean).map((segment, index, array) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
              "item": `https://pickstarai.com/${array.slice(0, index + 1).join('/')}`
            }))
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;